import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphService } from "../services/graphService";
import {
  EntityDetailResponse,
  MainEntity,
  extractMainEntity,
} from "../types/entityTypes";
import { queryKeys } from "./queries/keys";

interface UseEntityDetailResult {
  entityDetailResponse: EntityDetailResponse | null;
  mainEntity: MainEntity | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for fetching entity details via TanStack Query.
 *
 * @param entityId - The ID of the entity to fetch
 * @returns Object containing entity data, loading state, and error
 */
export const useEntityDetail = (
  entityId: string | null,
): UseEntityDetailResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.entities.detail(entityId!),
    queryFn: () => graphService.getEntityDetails(entityId!),
    enabled: !!entityId,
    staleTime: 2 * 60 * 1000,
  });

  const mainEntity = useMemo(() => {
    if (!data) return null;
    return extractMainEntity(data) ?? null;
  }, [data]);

  return {
    entityDetailResponse: data ?? null,
    mainEntity,
    loading: isLoading,
    error: error
      ? (error as Error).message || "Failed to load entity details"
      : null,
  };
};
